/** @file This file contains the store for SAFE program runs. */
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import Vue from 'vue';
import { Maybe } from '@codegrade/maybe';
import { eitherAsync, errors, expr, object } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import * as utils from '@/utils';
import getWSClient from '@/ws_client';
const storeBuilder = getStoreBuilder();
/** @returns A new unique listener ID. */
function newListenerId() {
    return uuidv4();
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
const makeInitialState = () => ({
    programs: {},
});
/** @returns An EitherAsync that will resolve to the `@codegrade/safe` module. */
function loadSafe() {
    return eitherAsync.importToEitherAsync(import('@codegrade/safe'), '@codegrade/safe');
}
const moduleBuilder = storeBuilder.module('safe', makeInitialState());
/** The store used to load and retrieve SAFE programs. */
export var SAFEStore;
(function (SAFEStore) {
    const setToError = moduleBuilder.commit((state, { programId, error, }) => {
        utils.vueSet(state.programs[programId], 'program', { tag: 'error', error });
    }, 'setToError');
    const addListener = moduleBuilder.dispatch(({ state }, { programId, listenerId, }) => {
        object.getItem(state.programs, programId).ifJust(found => {
            found.listeners.add(listenerId);
            utils.vueSet(state.programs, programId, found);
        });
    }, 'addListener');
    /**
     * Get a SAFE program by its id.
     *
     * Make sure you call `createOrGet` first for the given program id, as otherwise the
     * subscription might disappear from under you.
     */
    SAFEStore.getProgram = moduleBuilder.read(state => 
    /**
     * The actual getter used.
     *
     * @param arg0 The data used by the getter.
     * @returns The found program, or loading if none was found.
     */
    ({ programId, }) => object.getItem(state.programs, programId).caseOf({
        Nothing: () => ({ tag: 'loading' }),
        Just: object.attr('program'),
    }), 'getProgram');
    /** Maybe unusubscribe from the program topic, if no listeners are active anymore. */
    const maybeUnsubscribe = moduleBuilder.dispatch(({ state }, { programId, recursive = false, }) => {
        object
            .getItem(state.programs, programId)
            .filter(found => found.listeners.size === 0)
            .ifJust(found => {
            expr.match(found.program, 'tag', {
                error: () => {
                    utils.vueDelete(state.programs, programId);
                },
                loaded: ({ data }) => {
                    data.unsubscribe();
                    if (data.moreUpdatesExpected) {
                        utils.vueDelete(state.programs, programId);
                    }
                },
                loading: ({ data }) => {
                    // Prevent infinite recursion loops.
                    if (recursive) {
                        return;
                    }
                    data.then(() => maybeUnsubscribe({ programId, recursive: true }));
                },
            });
        });
    }, 'maybeUnsubscribe');
    /** Remove a listener for a program id, and unsubscribe if needed. */
    const removeListener = moduleBuilder.dispatch(({ state }, { programId, listenerId, }) => {
        object.getItem(state.programs, programId).ifJust(found => {
            found.listeners.delete(listenerId);
            utils.vueSet(state.programs, programId, found);
            maybeUnsubscribe({ programId });
        });
    }, 'removeListener');
    const addResult = moduleBuilder.commit((state, { programId, program, }) => {
        utils.vueSet(state.programs, programId, program);
    }, 'addResult');
    /**
     * Update something in the store.
     *
     * Be careful using this method, as it basically just overrides the type system and is used for
     * the subscription to make sure all mutations happen in a `commit`.
     */
    const updateResultInner = moduleBuilder.commit((_, { r, k, v, }) => {
        Vue.set(r, k, v);
    }, 'updateResultInner');
    const updateIfExists = moduleBuilder.commit((state, { programId, newProgram, }) => {
        object.getItem(state.programs, programId).caseOf({
            Just: old => utils.vueSet(state.programs, programId, { ...old, program: newProgram }),
            Nothing: () => utils.vueDelete(state.programs, programId),
        });
    }, 'updateResult');
    /**
     * Get the loader for the program.
     *
     * @param programId The program id to subscribe to and update the store state for.
     * @param retryUntil The final moment to retry subscribing.
     * @returns The loader for the safe program in the store.
     */
    async function getLoaderSafeProgramRun(programId, retryUntil) {
        try {
            const [safeMod, wsClient] = await Promise.all([
                eitherAsync.toPromise(loadSafe()),
                eitherAsync.toPromise(getWSClient()),
            ]);
            const runResult = await eitherAsync.toPromise(safeMod.run.SAFEProgramRunResult.create((data, onUpdate) => {
                const subscribe = wsClient.subscribeTo.safeProgramRun(data, onUpdate, error => setToError({ programId, error }));
                return retryUntil.caseOf({
                    Just: upTo => utils.eitherAsyncUtils.makeRetrying(subscribe, {
                        initialDelay: moment.duration(1, 's'),
                        backoffFactor: 1.1,
                        jitterFactor: 0.1,
                        shouldRetry: (_, err) => {
                            if (upTo.isBefore(moment.utc())) {
                                return false;
                            }
                            if (err.tag !== '$ServerFailure$') {
                                return false;
                            }
                            return err.err.result.code === 'DDB_SAFE_PROGRAM_NOT_FOUND';
                        },
                        fixupDelay: delay => {
                            const millis = delay.asMilliseconds();
                            return moment.duration(Math.min(5000, millis), 'ms');
                        },
                    }),
                    Nothing: () => subscribe,
                });
            }, { safeProgramId: programId }, (r, k, v) => updateResultInner({ r, k, v })));
            updateIfExists({ programId, newProgram: { tag: 'loaded', data: runResult } });
        }
        catch (error) {
            updateIfExists({
                programId,
                newProgram: { tag: 'error', error: errors.coerceToError(error) },
            });
        }
    }
    /**
     * Signal that you depend on a program subscription.
     *
     * This will load the subscription if it doesn't exist yet, or will add a listener if it does
     * already exist.
     *
     * @returns A promise that will resolve directly to a `LoadResult`. You should call its drop
     * function when you don't depend on the subscription anymore.
     */
    SAFEStore.addDependency = moduleBuilder.dispatch(({ state }, { programId, retryUntil, force = false, }) => {
        const found = Maybe.fromNullable(state.programs[programId]);
        const listenerId = newListenerId();
        found.caseOf({
            Just: () => {
                if (!force) {
                    addListener({ programId, listenerId });
                    return;
                }
                // Force loading of the safe program result here, even though we have one already.
                const listeners = new Set([...state.programs[programId].listeners, listenerId]);
                const loader = getLoaderSafeProgramRun(programId, retryUntil);
                addResult({
                    program: {
                        listeners,
                        program: { tag: 'loading', data: loader },
                    },
                    programId,
                });
            },
            Nothing: () => {
                const listeners = new Set([listenerId]);
                const loader = getLoaderSafeProgramRun(programId, retryUntil);
                addResult({
                    program: {
                        listeners,
                        program: { tag: 'loading', data: loader },
                    },
                    programId,
                });
            },
        });
        return { drop: () => removeListener({ programId, listenerId }) };
    }, 'createOrGet');
    const commitClearAll = moduleBuilder.commit(state => {
        utils.vueSet(state, 'programs', {});
    }, 'commitClearAll');
    utils.signal.AFTER_USER_CHANGED.registerSync(() => {
        commitClearAll();
    });
})(SAFEStore || (SAFEStore = {}));
